import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Modal from '../components/Modal';
import PhotoLightbox from '../components/PhotoLightbox';
import UpscaleConfigPanel from '../components/UpscaleConfigPanel';
import BakePreflight from '../components/BakePreflight';
import { useAllFootage, useTrips, renameAsset, trashAsset, isTrashed } from '../data/firestore';
import { getAssetPlaybackUrl } from '../data/uploadService';
import { InlineRename, DownloadButton, TrashControls } from '../components/AssetActions';
import RevisePrompt from '../components/RevisePrompt';
import { canReviseAsset } from '../utils/nlRevise';
import { useNow, renderJobLabel, renderJobProgress, assetRenderJob } from '../utils/eta';
import { fmtUSD } from '../utils/cost';
import firebase from '../firebaseConfig';
import { activeAdjustments } from '../utils/grade';
import { bust } from '../utils/cacheBust';

const API = process.env.REACT_APP_UPLOAD_API || '';

// Public, AI-fetchable preview URL for a variation (CDN derivatives, not the
// presign-on-demand raw). Used by the Compare "explain differences" panel.
const previewUrl = (a) =>
  a.displayUrl || a.derivatives?.magazine || a.derivatives?.fulljpeg || a.thumbUrl || '';

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};

// Recency (ms) for grouping + newest-first sort. We prefer the BAKE/refresh time
// (stillGrab.finishedAt) over the original grab/upload time: a still grabbed
// yesterday but re-baked today is "today's work" to the user, so it should land
// in the Today group and sort to the top. Without this, re-bakes stayed pinned to
// the original grab date and never appeared under Today. Falls back to the
// upload/grab time for plain uploads that were never baked in-app.
const bakedMs = (a) => (typeof a.stillGrab?.finishedAt === 'number' ? a.stillGrab.finishedAt : 0);
const createdMs = (a) => {
  const baked = bakedMs(a);
  if (baked > 0) return baked;
  const u = a.uploadedAt;
  if (u && typeof u.toMillis === 'function') return u.toMillis();
  if (u && typeof u.seconds === 'number') return u.seconds * 1000;
  if (typeof u === 'number') return u;
  return a.lineage?.addedAt || a.stillGrab?.requestedAt || 0;
};
// Compact "12m ago" / "3h ago" / "Jun 26" for the card, so fresh bakes are easy
// to spot at a glance. Absolute time lives in the title tooltip.
const fmtAgo = (ms) => {
  if (!ms) return '';
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d < 7) return `${d}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const dayKey = (ms) => {
  const d = new Date(ms || 0);
  return Number.isNaN(d.getTime()) ? '0' : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};
const dayLabel = (ms) => {
  if (!ms) return 'Undated';
  const full = new Date(ms).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
  const today = dayKey(Date.now());
  const yest = dayKey(Date.now() - 86400000);
  const k = dayKey(ms);
  if (k === today) return `Today · ${full}`;
  if (k === yest) return `Yesterday · ${full}`;
  return full;
};

// --- Shareable, human-readable photo URLs ---------------------------------
// The viewer reflects the open photo in ?photo=<slug>--<id>. The slug (built
// from the photo's name: clip + frame + variation) is purely cosmetic so the
// link reads sensibly; the doc id after the final "--" is the authoritative
// lookup key, so links keep working even if a photo is later renamed.
const slugify = (s) =>
  (s || '')
    .toString()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '');
const photoParamFor = (a) => {
  const slug = slugify(a?.name);
  return slug ? `${slug}--${a.id}` : a?.id || '';
};
const idFromParam = (p) => {
  if (!p) return null;
  const i = p.lastIndexOf('--');
  return i >= 0 ? p.slice(i + 2) : p;
};

// Identity of the SOURCE FRAME a still came from, so every grade/variation of the
// same frame groups together for comparison. Prefers the exact source clip +
// frame index; falls back to a "frame NNN" in the name, then to the grab time
// (so older grabs that predate frameIndex still match).
function frameKey(a) {
  const clip = a.lineage?.sourceAssetId || a.stillGrab?.sourceAssetId || '';
  if (!clip) return null;
  let frame = a.stillGrab?.frameIndex;
  if (!Number.isFinite(frame)) {
    const m = /frame\s+(\d{2,})\b/i.exec(a.name || '') || /\bf(\d{2,})\b/i.exec(a.name || '');
    if (m) frame = Number(m[1]);
  }
  if (!Number.isFinite(frame)) {
    const t = a.stillGrab?.timeSec ?? a.lineage?.sourceTimeSec;
    if (Number.isFinite(t)) frame = Math.round(t * 24); // assume 24fps when only time is known
  }
  return Number.isFinite(frame) ? `${clip}#${frame}` : null;
}
// Human label for a frame group, e.g. "A017_B031_0601BW · frame 273".
function frameLabel(a) {
  const src = (a.lineage?.sourceName || '').replace(/\.[^.]+$/, '');
  let frame = a.stillGrab?.frameIndex;
  if (!Number.isFinite(frame)) {
    const m = /frame\s+(\d{2,})\b/i.exec(a.name || '') || /\bf(\d{2,})\b/i.exec(a.name || '');
    if (m) frame = Number(m[1]);
  }
  return [src, Number.isFinite(frame) ? `frame ${frame}` : null].filter(Boolean).join(' · ') || (a.name || '');
}

function CardTags({ tags }) {
  if (!tags?.length) return null;
  return (
    <div className="cs-card-tags" title={tags.join(' · ')}>
      {tags.map((t) => (
        <span key={t} className="cs-card-tag">{t}</span>
      ))}
    </div>
  );
}

// Lazily presign a photo so we can show it from the private raw bucket. RAW
// assets prefer their developed thumbnail (the raw file isn't browser-viewable).
// `large` cards are ~2× wider than the 640px `thumbUrl`, so they load the
// high-res display derivative (3000px magazine) instead — otherwise the thumb
// gets upscaled and looks soft next to what opening the photo shows.
function PhotoCard({ asset, onOpen, onEdit, onCompare, onDetails, compareCount, large }) {
  const [url, setUrl] = useState('');
  const [revising, setRevising] = useState(false);
  const hiRes = asset.displayUrl || asset.derivatives?.magazine || asset.derivatives?.fulljpeg || '';
  useEffect(() => {
    let on = true;
    // Bust the browser cache on re-bakes (same S3 key, new pixels) so corrected
    // grades show without a hard refresh.
    if (large && hiRes) {
      setUrl(bust(hiRes, asset));
    } else if (asset.thumbUrl) {
      setUrl(bust(asset.thumbUrl, asset));
    } else if (asset.s3Key && !asset.isRaw) {
      getAssetPlaybackUrl(asset.s3Key).then((u) => on && setUrl(u)).catch(() => {});
    } else if (hiRes) {
      setUrl(bust(hiRes, asset));
    }
    return () => {
      on = false;
    };
  }, [asset.s3Key, asset.thumbUrl, asset.isRaw, hiRes, large, asset]);

  const rj = assetRenderJob(asset);
  const rjNow = useNow(!!rj);
  const rjCostUsd = rj?.costUsd != null ? rj.costUsd : rj?.costEstUsd;
  const rjProg = rj ? renderJobProgress(rj, rjNow) : 0;
  const isUpscaling = rj?.kind === 'upscale';
  const tags = Array.isArray(asset.tags) ? asset.tags.filter(Boolean) : [];

  return (
    <div className="cs-trip-card" style={{ cursor: 'pointer', position: 'relative' }} onClick={() => url && onOpen(asset)}>
      {asset.isRaw && <span className="cs-raw-badge">RAW</span>}
      {asset.upscale?.applied && (
        <span
          className="cs-up-badge"
          title={`AI-upscaled ${asset.upscale.scale || 2}× with ${asset.upscale.model || 'real-esrgan'} before grading`}
        >
          ⤴ {asset.upscale.scale || 2}× {asset.upscale.model === 'real-esrgan' ? 'ESRGAN' : asset.upscale.model === 'ultimate-sd' ? 'SD' : 'SwinIR'}
        </span>
      )}
      {asset.upscale?.pending && !asset.upscale?.applied && !isUpscaling && (
        <span
          className="cs-up-badge cs-up-badge-pending"
          title={`Upscale queued (${asset.upscale.scale || 2}× ${asset.upscale.model || 'real-esrgan'}). Run the upscale queue to process on the GPU.`}
        >
          ⤴ {asset.upscale.scale || 2}× queued…
        </span>
      )}
      {compareCount > 1 && (
        <button
          type="button"
          className="cs-compare-badge"
          title={`Compare ${compareCount} variations of this frame side by side`}
          onClick={(e) => { e.stopPropagation(); onCompare(asset); }}
        >
          ⊞ {compareCount}
        </button>
      )}
      <div
        className="cs-trip-cover"
        style={url
          ? { backgroundImage: `url(${url})`, position: 'relative' }
          : { display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, position: 'relative' }}
      >
        {!url && (rj ? '⏳' : '🖼')}
        {url && rj && (
          <span className="cs-rebake-badge" title="Re-baking this photo">
            <span className="cs-rebake-spin" aria-hidden />
            <span className="cs-rebake-label">{renderJobLabel(rj, rjNow)}</span>
          </span>
        )}
      </div>
      <div className="cs-trip-body">
        <InlineRename
          name={asset.name}
          onSave={(n) => renameAsset(asset.tripId, asset.id, n)}
          className="cs-card-title"
          style={{ fontSize: 14 }}
        />
        {rj && (
          <div className="cs-card-status">
            <span className="cs-card-status-msg">{renderJobLabel(rj, rjNow)}</span>
            <div className="cs-card-status-ctl">
              {isUpscaling ? (
                <span className="cs-card-cost" title="Live GPU upscale — progress streamed from ComfyUI">
                  🖥 GPU
                </span>
              ) : (
                <span className="cs-card-cost" title="Estimated compute cost (metered by wallclock when done)">
                  ~{fmtUSD(rjCostUsd || 0)} est
                </span>
              )}
            </div>
          </div>
        )}
        {rj && (
          <div className={`cs-card-prog${rjProg == null ? ' is-indet' : ''}`} title={rj.label || ''}>
            <span className="cs-card-prog-bar" style={rjProg == null ? undefined : { width: `${Math.max(3, rjProg)}%` }} />
          </div>
        )}
        <div className="cs-card-foot meta">
          <div className="cs-card-size" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {asset.width > 0 && asset.height > 0 && (
              <span
                className={asset.upscale?.applied ? 'cs-card-res is-upscaled' : 'cs-card-res'}
                title={asset.upscale?.applied ? `Upscaled ${asset.upscale.scale || ''}× — max resolution ${asset.width}×${asset.height}` : `Max resolution ${asset.width}×${asset.height}`}
              >
                {asset.width}×{asset.height}
              </span>
            )}
            {/* Size of the DELIVERED image = the full-res JPEG everyone views/downloads,
                so every card is comparable (a 3× and 4× both read as their JPEG — not
                one JPEG vs one much-larger lossless PNG). Never the 16-bit negative. */}
            <span>{fmtBytes(asset.fullBytes || asset.sizeBytes)}</span>
            {asset.fullPngBytes > 0 && (
              <span
                className="cs-card-png"
                title={`A lossless full-resolution PNG (${fmtBytes(asset.fullPngBytes)}) is available — open the photo and tap the PNG button to view or download it`}
              >
                +PNG {fmtBytes(asset.fullPngBytes)}
              </span>
            )}
            {bakedMs(asset) > 0 && (
              <span
                className="cs-card-baked"
                title={`Baked ${new Date(bakedMs(asset)).toLocaleString()}`}
              >
                · {fmtAgo(bakedMs(asset))}
              </span>
            )}
          </div>
          <div className="cs-card-actions">
            {compareCount > 1 && (
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                title={`Compare ${compareCount} variations of this frame`}
                onClick={(e) => { e.stopPropagation(); onCompare(asset); }}
              >
                ⊞ Compare
              </button>
            )}
            {onDetails && (asset.editManifest?.length > 0 || asset.editTree?.stages?.length > 0 || (asset.tripId && asset.id)) && (
              <button
                className="cs-btn cs-btn-ghost cs-btn-sm"
                title="Show details — trace every adjustment from the negative to the final grade"
                onClick={(e) => { e.stopPropagation(); onDetails(asset); }}
              >
                🌳 Details
              </button>
            )}
            {canReviseAsset(asset) && (
              <button
                className={`cs-btn cs-btn-ghost cs-btn-sm${revising ? ' is-on' : ''}`}
                title="Describe a change in words → bake a new version of this frame"
                onClick={(e) => { e.stopPropagation(); setRevising((v) => !v); }}
              >
                ✨ Revise
              </button>
            )}
            <DownloadButton s3Key={asset.s3Key} name={asset.name} label="⤓" title="Download original" />
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
            >
              Edit
            </button>
            <TrashControls
              what={`the photo “${asset.name}”`}
              onTrash={() => trashAsset(asset.tripId, asset.id)}
            />
          </div>
        </div>
        {revising && (
          <RevisePrompt asset={asset} compact autoFocus onDone={() => setRevising(false)} />
        )}
        <CardTags tags={tags} />
      </div>
    </div>
  );
}

// The side-by-side cells — one frame's variations. Shared by the Compare modal
// and the whole-page Compare view.
function CompareCells({ assets, onEdit, onOpen, noteFor }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
      {assets.map((a, i) => {
        const src = previewUrl(a);
        const tags = Array.isArray(a.tags) ? a.tags.filter(Boolean) : [];
        const note = noteFor ? noteFor(a, i) : null;
        return (
          <div key={a.id} className="cs-cmp-cell">
            {src ? (
              <img
                src={src}
                alt={a.name}
                loading="lazy"
                style={{ cursor: onOpen ? 'zoom-in' : 'default' }}
                onClick={() => onOpen && onOpen(a)}
              />
            ) : (
              <div className="cs-cmp-ph">{assetRenderJob(a) ? 'rendering…' : 'no preview'}</div>
            )}
            <div className="cs-cmp-name">{a.name}</div>
            <CardTags tags={tags} />
            {note && <div className="cs-cmp-cell-note">{note}</div>}
            <button className="cs-btn cs-btn-ghost cs-btn-sm" style={{ marginTop: 6 }} onClick={() => onEdit(a)}>
              🎨 Edit this one
            </button>
          </div>
        );
      })}
    </div>
  );
}

// Side-by-side comparison of every variation of one source frame (modal).
function CompareModal({ assets, onClose, onEdit, onOpen }) {
  return (
    <Modal title={`Compare · ${frameLabel(assets[0])} · ${assets.length} variations`} onClose={onClose} maxWidth={1600}>
      <CompareCells assets={assets} onEdit={onEdit} onOpen={onOpen} />
      <div className="cs-help" style={{ marginTop: 12 }}>
        All variations of the same source frame, side by side. Tags show each grade’s tweaks.
      </div>
    </Modal>
  );
}

// One frame's variations in the whole-page Compare view: the side-by-side cells
// plus an expandable right column where Grok explains how the variations differ
// (handy when two grades look almost identical to the eye).
function CompareRow({ label, assets, onEdit, onOpen }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null); // { summary, items:[{label,note}], recommendation }
  const [err, setErr] = useState('');

  const analyze = useCallback(async () => {
    setErr('');
    setBusy(true);
    try {
      const images = assets
        .map((a) => ({
          url: previewUrl(a),
          label: a.name,
          tags: Array.isArray(a.tags) ? a.tags.filter(Boolean) : [],
          // The real differentiator: each variation's actual grade params. The
          // CDN preview can hide a few points of vibrance/teal, so we hand Grok
          // the numbers too and let it ground its read in them.
          preset: a.grade?._preset ? { id: a.grade._preset.id, amount: a.grade._preset.amount } : null,
          params: activeAdjustments(a.grade).map((p) => ({
            key: p.key, label: p.label, value: p.value, unit: p.unit || '',
          })),
        }))
        .filter((im) => /^https?:\/\//.test(im.url));
      if (images.length < 2) throw new Error('Need at least 2 rendered previews to compare.');
      const user = firebase.auth().currentUser;
      const token = user ? await user.getIdToken() : null;
      const res = await fetch(`${API}/ai/color/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ images }),
      });
      if (!res.ok) {
        let detail = '';
        try { const b = await res.json(); detail = b?.error || ''; } catch { /* noop */ }
        throw new Error(detail || `request failed (${res.status})`);
      }
      setData(await res.json());
    } catch (e) {
      setErr(e.message || 'failed');
    } finally {
      setBusy(false);
    }
  }, [assets]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data && !busy) analyze();
  };

  // Match each AI note back to its variation card by label (fall back to order).
  const noteFor = (a, i) => {
    if (!data?.items?.length) return null;
    const byLabel = data.items.find((it) => it.label && it.label === a.name);
    return (byLabel || data.items[i] || null)?.note || null;
  };

  return (
    <section style={{ marginBottom: 36 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 19, margin: 0 }}>
          {label} <span className="cs-help" style={{ fontWeight: 400 }}>· {assets.length} variations</span>
        </h2>
        {(() => {
          const src = assets[0];
          const clipId = src?.lineage?.sourceAssetId || src?.stillGrab?.sourceAssetId;
          return clipId && src?.tripId ? (
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => navigate(`/clip-grade/${src.tripId}/${clipId}`)}
              title="Open the per-clip grading session for this source clip"
            >
              🎚 Grade clip
            </button>
          ) : null;
        })()}
        <button
          className={`cs-btn cs-btn-sm ${open ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
          style={{ marginLeft: assets[0]?.lineage?.sourceAssetId ? 0 : 'auto' }}
          onClick={toggle}
          title="Ask the AI colorist to explain how these variations differ"
        >
          {open ? '✕ Hide differences' : '🪄 Explain differences'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <CompareCells assets={assets} onEdit={onEdit} onOpen={onOpen} noteFor={open ? noteFor : null} />
        </div>
        {open && (
          <aside className="cs-cmp-explain">
            <div className="cs-cmp-explain-head">
              <span>🪄 AI difference report</span>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={analyze} disabled={busy} title="Re-analyze">
                {busy ? '…' : '↻'}
              </button>
            </div>
            {busy && <div className="cs-help">Looking at all {assets.length} grades…</div>}
            {err && !busy && (
              <div className="cs-cmp-explain-err">
                {err} <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={analyze}>Retry</button>
              </div>
            )}
            {data && !busy && (
              <>
                {data.summary && <p className="cs-cmp-explain-sum">{data.summary}</p>}
                {Array.isArray(data.items) && data.items.length > 0 && (
                  <ul className="cs-cmp-explain-list">
                    {data.items.map((it, i) => (
                      <li key={i}>
                        <strong>{it.label}</strong>
                        <span>{it.note}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {data.recommendation && (
                  <p className="cs-cmp-explain-rec"><strong>Pick:</strong> {data.recommendation}</p>
                )}
              </>
            )}
          </aside>
        )}
      </div>
    </section>
  );
}

export default function Photos() {
  const navigate = useNavigate();
  const { docs: footage } = useAllFootage(2000);
  const { docs: trips } = useTrips();
  const [searchParams, setSearchParams] = useSearchParams();
  const [navList, setNavList] = useState(null); // browsing context for the viewer
  const [openTree, setOpenTree] = useState(false); // open the viewer with the edit-tree panel shown
  const [compareKey, setCompareKey] = useState(null); // frameKey to compare
  const [view, setView] = useState('date'); // 'date' | 'all' | 'trip'
  const [dense, setDense] = useState('normal'); // 'normal' | 'large' (2 per row)
  const [cfgOpen, setCfgOpen] = useState(false); // upscale settings modal
  const [preflightOpen, setPreflightOpen] = useState(false); // bake preflight modal

  // The currently-planned expensive set (the Laurent 4× barrel proof set). The
  // preflight reviews the LIVE settings against this plan before we commit GPU.
  const bakePlan = {
    label: 'Laurent · Pascuales barrel 4× set',
    frames: 31,
    scale: 4,
    model: 'supir',
    lookName: 'B · Tucker v21 vivid',
    cropW: 0.36,
    cropH: 0.32,
    subjectCentered: true,
    rangeNote: 'f0–f600 · every 20 frames',
  };
  const openEditor = (a) => navigate(`/photo/${a.tripId}/${a.id}`);
  const gridClass = dense === 'large' ? 'cs-grid cs-grid-lg' : 'cs-grid';
  const photoId = idFromParam(searchParams.get('photo'));

  const setPhotoParam = (asset, replace) => {
    const p = new URLSearchParams(searchParams);
    if (asset) p.set('photo', photoParamFor(asset)); else p.delete('photo');
    setSearchParams(p, { replace });
  };
  // Open the fullscreen viewer on `asset`. The open photo lives in the URL
  // (?photo=<readable-slug>--<id>) so it's shareable and the browser Back button
  // closes it; we remember the list the user is browsing so swipes step through it.
  const openLightbox = (list, asset, withTree = false) => {
    setNavList(list && list.length ? list : [asset]);
    setOpenTree(withTree);
    setPhotoParam(asset, false); // push a history entry → Back closes
  };
  const closeLightbox = () => { setOpenTree(false); setPhotoParam(null, true); };

  const photos = useMemo(
    () => (footage || [])
      .filter((a) => a.kind === 'photo' && !isTrashed(a))
      .sort((a, b) => createdMs(b) - createdMs(a)), // newest bake first
    [footage]
  );

  // Map every frame key → its variations, so each card knows how many siblings
  // it has and Compare can pull them instantly.
  const frameGroups = useMemo(() => {
    const m = new Map();
    for (const p of photos) {
      const k = frameKey(p);
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    }
    // Newest variation first within a group.
    for (const list of m.values()) list.sort((a, b) => createdMs(b) - createdMs(a));
    return m;
  }, [photos]);

  const compareAssets = compareKey ? frameGroups.get(compareKey) || [] : [];

  // The viewer is driven by the URL: resolve the ?photo id against the list the
  // user was browsing (falls back to all photos on a fresh deep-link / refresh).
  const viewer = useMemo(() => {
    if (!photoId) return null;
    const list = navList && navList.some((a) => a.id === photoId) ? navList : photos;
    const index = list.findIndex((a) => a.id === photoId);
    return index >= 0 ? { list, index } : null;
  }, [photoId, navList, photos]);

  // Every frame that has more than one variation, newest group first — drives
  // the whole-page Compare view.
  const compareGroups = useMemo(() => {
    const groups = [...frameGroups.entries()]
      .map(([key, list]) => ({ key, list }))
      .filter((g) => g.list.length > 1);
    groups.sort((a, b) => createdMs(b.list[0]) - createdMs(a.list[0]));
    return groups;
  }, [frameGroups]);

  const byTrip = useMemo(() => {
    const groups = (trips || []).map((t) => ({ trip: t, photos: photos.filter((p) => p.tripId === t.id) }));
    const known = new Set((trips || []).map((t) => t.id));
    const orphans = photos.filter((p) => !known.has(p.tripId));
    return { groups: groups.filter((g) => g.photos.length), orphans };
  }, [photos, trips]);

  const byDate = useMemo(() => {
    const map = new Map();
    for (const p of photos) {
      const ms = createdMs(p);
      const k = dayKey(ms);
      if (!map.has(k)) map.set(k, { ms, label: dayLabel(ms), photos: [] });
      map.get(k).photos.push(p);
    }
    return [...map.values()].sort((a, b) => b.ms - a.ms);
  }, [photos]);

  const openCompare = (a) => { setCompareKey(frameKey(a)); closeLightbox(); };
  const compareCountFor = (a) => (frameGroups.get(frameKey(a)) || []).length;

  const renderGrid = (list) => (
    <div className={gridClass}>
      {list.map((a) => (
        <PhotoCard
          key={a.id}
          asset={a}
          large={dense === 'large'}
          compareCount={compareCountFor(a)}
          onOpen={(x) => openLightbox(list, x)}
          onDetails={(x) => openLightbox(list, x, true)}
          onCompare={openCompare}
          onEdit={() => openEditor(a)}
        />
      ))}
    </div>
  );

  return (
    <div className="cs-page">
      <div className="cs-page-head">
        <div>
          <h1>Photos</h1>
          <span className="cs-help">Stills from your trips · grab more from any clip in a trip</span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="cs-segment">
            <button className={view === 'date' ? 'on' : ''} onClick={() => setView('date')}>By date</button>
            <button className={view === 'all' ? 'on' : ''} onClick={() => setView('all')}>All</button>
            <button className={view === 'trip' ? 'on' : ''} onClick={() => setView('trip')}>By trip</button>
            <button
              className={view === 'compare' ? 'on' : ''}
              onClick={() => setView('compare')}
              title="Auto-arrange every frame that has multiple grade variations side by side"
            >
              ⊞ Compare{compareGroups.length ? ` (${compareGroups.length})` : ''}
            </button>
          </div>
          <div className="cs-segment" title="Card size — Large shows 2 per row for side-by-side comparison">
            <button className={dense === 'normal' ? 'on' : ''} onClick={() => setDense('normal')}>Grid</button>
            <button className={dense === 'large' ? 'on' : ''} onClick={() => setDense('large')}>Large</button>
          </div>
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => setCfgOpen(true)}
            title="Tune the AI upscaler + pipeline knobs (SUPIR strength, prompts, timeouts) — stored in Firestore, applied with no deploy"
          >
            ⚙ Upscale settings
          </button>
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => setPreflightOpen(true)}
            title="Preflight: review every effective setting + time/cost estimate before committing an expensive 4× set"
          >
            ✅ Bake preflight
          </button>
        </div>
      </div>
      {cfgOpen && <UpscaleConfigPanel onClose={() => setCfgOpen(false)} />}
      {preflightOpen && (
        <BakePreflight
          plan={bakePlan}
          onClose={() => setPreflightOpen(false)}
          onProceed={() => { /* approval recorded to Firestore; operator launches the set */ }}
        />
      )}

      {footage === null ? (
        <div>Loading…</div>
      ) : photos.length === 0 ? (
        <div className="cs-empty">
          No photos yet. Upload photos to a trip, or use “Grab frame” on a clip to
          export a full-resolution still.
        </div>
      ) : view === 'compare' ? (
        <div style={{ marginTop: 8 }}>
          {compareGroups.length === 0 ? (
            <div className="cs-empty">
              No frames with multiple variations yet. Grab the same frame with different grades
              (or re-bake a frame) and they’ll line up here automatically.
            </div>
          ) : (
            compareGroups.map(({ key, list }) => (
              <CompareRow
                key={key}
                label={frameLabel(list[0])}
                assets={list}
                onEdit={openEditor}
                onOpen={(x) => openLightbox(list, x)}
              />
            ))
          )}
        </div>
      ) : view === 'all' ? (
        <div style={{ marginTop: 8 }}>{renderGrid(photos)}</div>
      ) : view === 'date' ? (
        <div style={{ marginTop: 8 }}>
          {byDate.map((g) => (
            <section key={g.label} style={{ marginBottom: 36 }}>
              <h2 style={{ fontSize: 19, marginBottom: 12 }}>
                {g.label} <span className="cs-help" style={{ fontWeight: 400 }}>· {g.photos.length}</span>
              </h2>
              {renderGrid(g.photos)}
            </section>
          ))}
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          {byTrip.groups.map(({ trip, photos: list }) => (
            <section key={trip.id} style={{ marginBottom: 36 }}>
              <h2 style={{ fontSize: 19, marginBottom: 12 }}>{trip.name}</h2>
              {renderGrid(list)}
            </section>
          ))}
          {byTrip.orphans.length > 0 && (
            <section style={{ marginBottom: 36 }}>
              <h2 style={{ fontSize: 19, marginBottom: 12 }}>Other</h2>
              {renderGrid(byTrip.orphans)}
            </section>
          )}
        </div>
      )}

      {viewer && (
        <PhotoLightbox
          list={viewer.list}
          index={viewer.index}
          onIndex={(i) => { const a = viewer.list[i]; if (a) setPhotoParam(a, true); }}
          compareCountFor={compareCountFor}
          onClose={closeLightbox}
          onCompare={openCompare}
          onEdit={(a) => openEditor(a)}
          initialShowTree={openTree}
        />
      )}
      {compareAssets.length > 0 && (
        <CompareModal
          assets={compareAssets}
          onClose={() => setCompareKey(null)}
          onEdit={(a) => openEditor(a)}
          onOpen={(x) => { const list = compareAssets; setCompareKey(null); openLightbox(list, x); }}
        />
      )}
    </div>
  );
}
